import { Scheduler } from '@aldabil/react-scheduler'
import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
    fetchWithAuth,
    getCSRFToken,
    getAssociatedTeams,
    isAssociateManagerOrAbove,
    isICCSupport,
    getUsername,
} from '../auth'
import { Message, Icon } from 'semantic-ui-react'
import { getSchedule } from '../common/apiClient'
import { statusMap } from '../common/helpers'
import { LoadingWrapper, ScheduleEventEditor } from '../common'
import './Members.css'
import '../../css/common.css'

export { eventTypes } from '../common/ScheduleEventEditor'

const STATUS_COLORS = {
    pending:  '#AAAAAA',
    approved: 'var(--tds-blue)',
    denied:   '#FF5555',
}

const SCHEDULER_CONFIG = {
    height: 700,
    weekDays: [0, 1, 2, 3, 4, 5, 6],
    weekStartOn: 0,
    startHour: 6,
    endHour: 20,
    step: 60,
}

export const MemberSchedule = () => {
    const { id } = useParams()
    const [events, setEvents] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [vacationInfo, setVacationInfo] = useState([])
    const [calendarMemberEvents, setCalendarMemberEvents] = useState([])
    const [teamVacationLimits, setTeamVacationLimits] = useState({})
    const [memberTeamIds, setMemberTeamIds] = useState([])
    const [editContextReady, setEditContextReady] = useState(
        () => getUsername() === id || isICCSupport()
    )

    const isSelf = getUsername() === id
    const canEdit =
        isSelf
        || isICCSupport()
        || (isAssociateManagerOrAbove() && memberTeamIds.length > 0)

    function isEventDeletable(event) {
        if (isAssociateManagerOrAbove()) {
            const midnightToday = new Date()
            midnightToday.setHours(0, 0, 0, 0)
            return event.approvalStatus === 'approved'
                && event.type !== 'teamEvent'
                && new Date(event.start) > midnightToday
        }
        const midnightTonight = new Date()
        midnightTonight.setHours(23, 59, 59, 999)
        return event.type !== 'teamEvent' && new Date(event.start) > midnightTonight
    }

    function getVacationLimitError(startDate, endDate) {
        if (isICCSupport() || !startDate || memberTeamIds.length === 0) return null

        const schedulingMemberId = id
        const rangeStart = new Date(startDate)
        rangeStart.setHours(0, 0, 0, 0)
        const rangeEnd = new Date(endDate || startDate)
        rangeEnd.setHours(0, 0, 0, 0)

        for (let day = new Date(rangeStart); day <= rangeEnd; day.setDate(day.getDate() + 1)) {
            const dayStart = new Date(day)
            dayStart.setHours(0, 0, 0, 0)
            const dayEnd = new Date(day)
            dayEnd.setHours(23, 59, 59, 999)

            for (const teamId of memberTeamIds) {
                const limit = teamVacationLimits[teamId]
                if (!limit) continue

                const overlappingCount = calendarMemberEvents.filter(e => {
                    const eventMemberId = e.member?.userId || e.member?.user_id
                    return eventMemberId !== schedulingMemberId &&
                        e.teams?.some(t => t.id === teamId) &&
                        (e.type === 'pto' || e.type === 'sick') &&
                        new Date(e.start) < dayEnd &&
                        new Date(e.end) > dayStart
                }).length

                if (overlappingCount >= limit) {
                    return 'No Time Off available. Please work with your supervisor if needed.'
                }
            }
        }
        return null
    }

    async function getEvents() {
        setLoading(true)
        const resp = await getSchedule(id)
        if (resp.status === 404) {
            setError("This member does not exist.")
        } else if (!resp.ok) {
            setError("Something went wrong.")
        } else {
            const respJson = await resp.json()
            setEvents(respJson.map((event) => ({
                event_id: event.id,
                start: new Date(event.start),
                end: new Date(event.end),
                title: event.title,
                notes: event.notes,
                type: event.type,
                color: STATUS_COLORS[event.approvalStatus],
                eventColor: STATUS_COLORS[event.approvalStatus],
                approvalStatus: event.approvalStatus,
                designatedApprover: event.designatedApprover,
            })))
        }
        setLoading(false)
    }

    async function fetchCalendarContext() {
        const API = process.env.REACT_APP_ICC_TOOL_API_HOST
        const isSupport = isICCSupport()
        const myTeams = getAssociatedTeams()
        const myTeamIds = isSupport ? null : myTeams.map(t => t.id)

        try {
            const teamsResp = await fetchWithAuth(`${API}teams/`, {
                headers: { 'X-CSRFToken': getCSRFToken() },
                credentials: 'include',
            })
            if (!teamsResp.ok) return

            const allTeams = await teamsResp.json()
            const visibleTeams = isSupport
                ? allTeams
                : allTeams.filter(t => myTeamIds.includes(t.id))

            const memberTeams = visibleTeams.filter(t =>
                (t.members || []).includes(id)
            )
            const teamIds = memberTeams.map(t => t.id)
            setMemberTeamIds(teamIds)
            setTeamVacationLimits(Object.fromEntries(
                memberTeams
                    .filter(t => t.teamType?.vacationLimit != null)
                    .map(t => [t.id, t.teamType.vacationLimit])
            ))

            if (teamIds.length === 0) {
                setCalendarMemberEvents([])
                setVacationInfo([])
                return
            }

            const calendarResp = await fetchWithAuth(
                `${API}events/calendar/?team_ids=${teamIds.join(',')}`,
                { headers: { 'X-CSRFToken': getCSRFToken() }, credentials: 'include' }
            )
            if (!calendarResp.ok) return

            const { memberEvents = [] } = await calendarResp.json()
            setCalendarMemberEvents(memberEvents)

            const today = new Date(); today.setHours(0, 0, 0, 0)
            const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1)
            const info = teamIds
                .map(teamId => {
                    const team = memberTeams.find(t => t.id === teamId)
                    const limit = team?.teamType?.vacationLimit
                    if (!limit) return null
                    const usedToday = memberEvents.filter(e =>
                        e.teams?.some(t => t.id === teamId) &&
                        (e.type === 'pto' || e.type === 'sick') &&
                        new Date(e.start) < tomorrow &&
                        new Date(e.end) > today
                    ).length
                    return { teamName: team.name, limit, usedToday }
                })
                .filter(Boolean)
            setVacationInfo(info)
        } catch {
            // non-critical — vacation info is supplemental
        } finally {
            setEditContextReady(true)
        }
    }

    async function deleteEvent(eventId) {
        setError('')
        const resp = await fetchWithAuth(
            `${process.env.REACT_APP_ICC_TOOL_API_HOST}members/${id}/schedule/${eventId}/`,
            {
                headers: { 'X-CSRFToken': getCSRFToken(), 'Content-Type': 'application/json' },
                credentials: 'include',
                method: 'DELETE',
            }
        )
        if (!resp.ok) setError("Something went wrong.")
        else refreshSchedule()
    }

    async function escalateEvent(eventId) {
        setError('')
        const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'events/' + eventId + '/escalate/', {
            headers: { 'X-CSRFToken': getCSRFToken() },
            credentials: 'include',
            method: 'POST'
        })
        if (!resp.ok) setError("Something went wrong.")
        else refreshSchedule()
    }

    function refreshSchedule() {
        getEvents()
        fetchCalendarContext()
    }

    useEffect(() => {
        refreshSchedule()
    }, [id])

    const editorExistingEvents = events.map(e => ({
        event_id: e.event_id,
        type: e.type,
        start: e.start,
        end: e.end,
    }))

    return (
        <LoadingWrapper loading={loading} text="Loading calendar">
            <div>
                <div style={{ width: "100%", display: "flex", justifyContent: "center", marginBottom: "1rem" }}>
                    {error && <Message style={{ width: "fit-content" }} negative>{error}</Message>}
                </div>
                {vacationInfo.length > 0 && (
                    <Message info style={{ marginBottom: '1rem' }}>
                        <Message.Header>Today's Vacation Availability</Message.Header>
                        {vacationInfo.map(({ teamName, limit, usedToday }) => {
                            const available = Math.max(0, limit - usedToday)
                            return (
                                <p key={teamName}>
                                    <strong>{teamName}:</strong>{' '}
                                    {available} of {limit} slot{limit !== 1 ? 's' : ''} available today
                                </p>
                            )
                        })}
                    </Message>
                )}
                {editContextReady && (
                    <Scheduler
                        view="week"
                        height={SCHEDULER_CONFIG.height}
                        editable={canEdit}
                        deletable={canEdit ? isEventDeletable : false}
                        draggable={false}
                        events={events}
                        onDelete={canEdit ? deleteEvent : undefined}
                        customEditor={canEdit ? (scheduler) => (
                            <ScheduleEventEditor
                                scheduler={scheduler}
                                memberId={id}
                                eventId={scheduler.edited ? String(scheduler.edited.event_id) : null}
                                titleField="title"
                                onSuccess={refreshSchedule}
                                setError={setError}
                                checkVacationLimit={
                                    scheduler.edited
                                        ? null
                                        : (start, end) => getVacationLimitError(start, end)
                                }
                                existingEvents={editorExistingEvents}
                            />
                        ) : undefined}
                        week={{
                            weekDays: SCHEDULER_CONFIG.weekDays,
                            weekStartOn: SCHEDULER_CONFIG.weekStartOn,
                            startHour: SCHEDULER_CONFIG.startHour,
                            endHour: SCHEDULER_CONFIG.endHour,
                            step: SCHEDULER_CONFIG.step,
                        }}
                        month={{
                            weekDays: SCHEDULER_CONFIG.weekDays,
                            weekStartOn: SCHEDULER_CONFIG.weekStartOn,
                            startHour: SCHEDULER_CONFIG.startHour,
                            endHour: SCHEDULER_CONFIG.endHour,
                            disableGoToDay: true,
                        }}
                        day={{
                            startHour: SCHEDULER_CONFIG.startHour,
                            endHour: SCHEDULER_CONFIG.endHour,
                            step: SCHEDULER_CONFIG.step,
                        }}
                        eventRenderer={({ event, ...props }) => (
                            <div className="event-on-calendar" style={{ backgroundColor: event.eventColor }} {...props}>
                                <div className="overflow-text">{statusMap[event.type] ? statusMap[event.type] : event.type} - {event.title}</div>
                                <div className="overflow-text">{event.start.toLocaleTimeString("en-US", { timeStyle: "short" })} to {event.end.toLocaleTimeString("en-US", { timeStyle: "short" })}</div>
                            </div>
                        )}
                        viewerExtraComponent={(fields, event) => {
                            if (event.approvalStatus !== 'pending') return null
                            const message = (event.designatedApprover?.status === 'active' || !event.designatedApprover)
                                ? <div>This absence is waiting on approval from <Link to={'/members/' + event.designatedApprover?.id + '/view'}>{event.designatedApprover?.name}</Link></div>
                                : <div>The designated approver <Link to={'/members/' + event.designatedApprover?.id + '/view'}>{event.designatedApprover?.name}</Link> is out right now. You may <Link onClick={() => escalateEvent(event.event_id)}>escalate approval to their supervisor.</Link></div>
                            return (
                                <div style={{ display: "flex" }}>
                                    <Icon className="exclamation circle" color="red" />
                                    {message}
                                </div>
                            )
                        }}
                    />
                )}
            </div>
        </LoadingWrapper>
    )
}
